import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/api';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CircleNotch, FloppyDisk, Plus } from '@phosphor-icons/react';
import { toast } from 'sonner';

/** Tariffario: prezzo per ogni prestazione della professione scelta. */
export const TariffEditor = ({ professionCode, onSaved, allowedNames = null }) => {
  const [items, setItems] = useState([]);
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [showPropose, setShowPropose] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPrice, setNewPrice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Solo le specializzazioni selezionate dal professionista entrano nel tariffario
      let allowed = allowedNames && allowedNames.length ? allowedNames : null;
      if (!allowed) {
        try {
          const me = await api.get('/api/professionals/me');
          allowed = me.data?.specializations?.length ? me.data.specializations : null;
        } catch { allowed = null; }
      }
      const q = professionCode ? `?profession_code=${encodeURIComponent(professionCode)}` : '';
      const r = await api.get(`/api/professionals/me/services${q}`);
      const all = r.data.items || [];
      const list = allowed ? all.filter((s) => allowed.includes(s.name)) : all;
      setItems(list);
      setPrices(Object.fromEntries(list.map((s) => [
        s.service_id, s.price_eur != null ? String(s.price_eur) : '',
      ])));
    } catch { setItems([]); } finally { setLoading(false); }
  }, [professionCode, allowedNames]);

  const loadProposals = useCallback(async () => {
    try {
      const r = await api.get('/api/professionals/me/service-proposals');
      setProposals((r.data.items || []).filter((x) => x.status === 'pending'));
    } catch { setProposals([]); }
  }, []);

  useEffect(() => { load(); loadProposals(); }, [load, loadProposals]);

  const propose = async () => {
    if (newName.trim().length < 3) { toast.error('Indica il nome della prestazione'); return; }
    try {
      await api.post('/api/professionals/me/service-proposals', {
        name: newName.trim(),
        price_eur: newPrice ? parseFloat(newPrice) : null,
      });
      toast.success('Proposta inviata: sarà valutata dall\'amministratore');
      setNewName(''); setNewPrice(''); setShowPropose(false);
      loadProposals();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio proposta');
    }
  };

  const saveAll = async () => {
    const changed = items.filter((s) => {
      const v = prices[s.service_id];
      return v !== '' && v != null && parseFloat(v) !== s.price_eur;
    });
    if (changed.length === 0) { toast.info('Nessuna modifica da salvare'); return; }
    setSaving(true);
    try {
      for (const s of changed) {
        await api.put(`/api/professionals/me/services/${s.service_id}`, { price_eur: parseFloat(prices[s.service_id]) });
      }
      toast.success(`Tariffario aggiornato: ${changed.length} prestazioni`);
      await load();
      onSaved?.(changed.length);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio tariffario');
    } finally { setSaving(false); }
  };

  const filled = items.filter((s) => prices[s.service_id] !== '' && prices[s.service_id] != null).length;

  if (loading) {
    return <div className="flex justify-center py-8"><CircleNotch size={24} className="animate-spin text-primary" /></div>;
  }

  return (
    <div data-testid="tariff-editor">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <p className="text-xs text-foreground/60">
          Prestazioni compilate: <strong>{filled}</strong>/{items.length}
        </p>
        <Button size="sm" className="rounded-full h-8" onClick={saveAll} disabled={saving} data-testid="tariff-save-all">
          <FloppyDisk size={15} className="mr-1.5" /> {saving ? 'Salvataggio…' : 'Salva tariffario'}
        </Button>
      </div>

      <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
        {items.map((s) => (
          <div key={s.service_id} className="flex items-center gap-3 border border-border rounded-xl px-3 py-2" data-testid={`tariff-row-${s.service_id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm truncate">{s.name}</p>
              {s.default_price_eur != null && (
                <p className="text-[11px] text-foreground/50">Prezzo suggerito: €{s.default_price_eur}</p>
              )}
            </div>
            {s.price_eur != null && <Badge variant="outline" className="text-[10px]">attivo</Badge>}
            <div className="flex items-center gap-1">
              <span className="text-xs text-foreground/50">€</span>
              <Input
                type="number" min="0" step="1" className="w-24 h-9"
                value={prices[s.service_id] ?? ''}
                placeholder={s.default_price_eur != null ? String(s.default_price_eur) : '0'}
                onChange={(e) => setPrices((p) => ({ ...p, [s.service_id]: e.target.value }))}
                data-testid={`tariff-price-${s.service_id}`}
              />
            </div>
          </div>
        ))}
        {proposals.map((p) => (
          <div key={p.proposal_id} className="flex items-center gap-3 border border-amber-200 bg-amber-50 rounded-xl px-3 py-2" data-testid={`proposal-row-${p.proposal_id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm truncate">{p.name}</p>
              <p className="text-[11px] text-amber-800">In attesa di approvazione dall'amministratore</p>
            </div>
            {p.price_eur != null && <span className="text-xs text-foreground/60">€{p.price_eur}</span>}
          </div>
        ))}

        {items.length === 0 && proposals.length === 0 && (
          <p className="text-sm text-foreground/60">
            Nessuna prestazione da tariffare: seleziona le specializzazioni nel tuo profilo per vederle qui.
          </p>
        )}
      </div>

      <div className="mt-4 border-t border-border pt-3">
        {!showPropose ? (
          <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => setShowPropose(true)} data-testid="propose-service-btn">
            <Plus size={14} className="mr-1.5" /> Proponi una prestazione non in elenco
          </Button>
        ) : (
          <div className="space-y-2" data-testid="propose-service-form">
            <p className="text-xs text-foreground/60">
              Indica la prestazione che offri e non trovi in elenco: l'amministratore la valuterà e,
              se approvata, entrerà nel catalogo e nel tuo tariffario.
            </p>
            <div className="flex flex-wrap gap-2">
              <Input
                className="flex-1 min-w-[220px] h-9" placeholder="Nome della prestazione"
                value={newName} onChange={(e) => setNewName(e.target.value)}
                data-testid="propose-name-input"
              />
              <Input
                type="number" min="0" className="w-28 h-9" placeholder="€ prezzo"
                value={newPrice} onChange={(e) => setNewPrice(e.target.value)}
                data-testid="propose-price-input"
              />
              <Button size="sm" className="rounded-full h-9" onClick={propose} data-testid="propose-submit-btn">Invia proposta</Button>
              <Button size="sm" variant="ghost" className="rounded-full h-9" onClick={() => setShowPropose(false)}>Annulla</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TariffEditor;
